// src/services/appConfigApi.js
//
// Uzaktan konfigürasyon (mobil uygulamanın açılışta çektiği ayarlar) uçları ve
// doğrulama yardımcıları.
//
// Sözleşme (UI metinleri buna göre yazılmıştır):
//   - Değer her zaman METİN olarak saklanır; mobil yanıtta hangi JSON tipine
//     çevrileceğini valueType belirler. Sunucu yazarken doğrular.
//   - DELETE ucu bilerek yok: anahtarı silmek eski uygulama sürümlerinde o ayarı
//     sessizce kaybettirir. Yayından kaldırmak = enabled: false.
//   - group / key sonradan değiştirilemez (yeniden adlandırmak da silmekle aynı).
//   - PUT ve PATCH aynı davranır: yalnızca gönderilen alanlar değişir.

import apiClient from './ApiClient';

export const VALUE_TYPES = [
    { value: 'STRING', label: 'Metin', hint: 'Serbest metin.' },
    { value: 'INT', label: 'Tam sayı', hint: 'Örn. 9' },
    { value: 'DOUBLE', label: 'Ondalıklı sayı', hint: 'Nokta ile: 1.5' },
    { value: 'BOOL', label: 'Açık / kapalı', hint: 'true ya da false.' },
    { value: 'JSON', label: 'JSON', hint: 'Yalnızca obje ya da dizi: {"enabled": true}' },
];

export const CONFIG_PLATFORMS = [
    { value: 'all', label: 'Tümü' },
    { value: 'ios', label: 'iOS' },
    { value: 'android', label: 'Android' },
];

export const configPlatformLabel = (value) =>
    CONFIG_PLATFORMS.find((p) => p.value === value)?.label || value || '—';

// --- Doğrulama -----------------------------------------------------------
// Sunucu zaten reddeder; buradaki kopya 400 almadan önce hatayı yerinde göstermek için.

export const IDENTIFIER_PATTERN = /^[a-z0-9_]+$/;
export const MAX_GROUP_LENGTH = 50;
export const MAX_KEY_LENGTH = 100;
export const MAX_VALUE_LENGTH = 20000;

const TR_ASCII = { ç: 'c', ğ: 'g', ı: 'i', ö: 'o', ş: 's', ü: 'u', â: 'a', î: 'i', û: 'u' };

/**
 * Yazılan metni group/key kuralına yaklaştırır: küçük harf, Türkçe harfler ASCII,
 * boşluk ve tire alt çizgi, kalan her şey atılır. "Bakım Modu" → "bakim_modu".
 */
export const toIdentifier = (text) =>
    String(text || '')
        .toLocaleLowerCase('tr-TR')
        .replace(/[çğıöşüâîû]/g, (ch) => TR_ASCII[ch])
        .replace(/[\s-]+/g, '_')
        .replace(/[^a-z0-9_]/g, '');

export const validateIdentifier = (value, { label, max }) => {
    if (!value) return `${label} gerekli.`;
    if (!IDENTIFIER_PATTERN.test(value)) return `${label} yalnızca küçük harf, rakam ve alt çizgi içerebilir.`;
    if (value.length > max) return `${label} en fazla ${max} karakter olabilir.`;
    return null;
};

const INT_PATTERN = /^-?\d+$/;
const DOUBLE_PATTERN = /^-?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;

/** Değer seçilen tipe uyuyorsa null, uymuyorsa kullanıcıya gösterilecek metin döner. */
export const validateValue = (value, valueType) => {
    const text = String(value ?? '');
    if (text.length > MAX_VALUE_LENGTH) return `Değer en fazla ${MAX_VALUE_LENGTH.toLocaleString('tr-TR')} karakter olabilir.`;

    switch (valueType) {
        case 'INT':
            return INT_PATTERN.test(text.trim()) ? null : 'Tam sayı girin. Örn. 9';
        case 'DOUBLE':
            if (/^-?\d+,\d+$/.test(text.trim())) return 'Ondalık ayırıcı nokta olmalı: 1.5';
            return DOUBLE_PATTERN.test(text.trim()) ? null : 'Sayı girin. Örn. 1.5';
        case 'BOOL':
            return text === 'true' || text === 'false' ? null : 'Değer true ya da false olmalı.';
        case 'JSON': {
            let parsed;
            try {
                parsed = JSON.parse(text);
            } catch (e) {
                return `Geçersiz JSON: ${e.message}`;
            }
            if (parsed === null || typeof parsed !== 'object') {
                return 'JSON tipi yalnızca obje ya da dizi kabul eder. Tek bir sayı ya da metin için uygun tipi seçin.';
            }
            return null;
        }
        default:
            return null;
    }
};

// --- Hata kodları --------------------------------------------------------

// Metinler değişebilir, kodlar sözleşmedir. Burada olmayan kodda sunucunun
// `error` metni gösterilir.
const ERROR_MESSAGES = {
    INVALID_GROUP: 'Grup adı geçersiz: yalnızca küçük harf, rakam ve alt çizgi; en fazla 50 karakter.',
    INVALID_KEY: 'Anahtar geçersiz: yalnızca küçük harf, rakam ve alt çizgi; en fazla 100 karakter.',
    INVALID_VALUE_TYPE: 'Tanınmayan değer tipi.',
    INVALID_PLATFORM: 'Tanınmayan platform.',
    INVALID_INT_VALUE: 'Değer tam sayı değil.',
    INVALID_DOUBLE_VALUE: 'Değer sayı değil.',
    INVALID_BOOL_VALUE: 'Değer true ya da false olmalı.',
    INVALID_JSON_VALUE: 'Değer geçerli bir JSON obje ya da dizisi değil.',
    VALUE_TOO_LONG: 'Değer 20.000 karakteri aşıyor.',
};

export const readApiError = (err, fallback = 'Beklenmeyen bir hata oluştu.') => {
    const data = err?.response?.data;
    const code = data?.code || null;

    if (code && ERROR_MESSAGES[code]) return { code, message: ERROR_MESSAGES[code] };

    const text = data?.error || data?.message;
    if (text) return { code, message: text };

    const status = err?.response?.status;
    if (status === 403) return { code, message: 'Bu işlem için yetkiniz yok.' };
    if (status === 404) return { code, message: 'Kayıt bulunamadı.' };

    return { code, message: fallback };
};

// BaseResponse zarfı: { status, data }. Düz gövde de tolere edilir.
const unwrap = (responseData) => {
    if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        return responseData.data;
    }
    return responseData;
};

// --- Uçlar ---------------------------------------------------------------

/** GET /v1/admin/app-config — pasif kayıtlar dahil, group sonra key sırasında. */
export const fetchAppConfigs = async () => {
    const response = await apiClient.get('/v1/admin/app-config');
    const data = unwrap(response.data);
    return Array.isArray(data) ? data : [];
};

/** POST /v1/admin/app-config — aynı group.key varsa 409 CONFIG_KEY_EXISTS. */
export const createAppConfig = async (payload) => {
    const response = await apiClient.post('/v1/admin/app-config', payload);
    return unwrap(response.data);
};

/** PATCH /v1/admin/app-config/{id} — yalnızca gönderilen alanlar değişir; group/key yok sayılır. */
export const updateAppConfig = async (id, patch) => {
    const response = await apiClient.patch(`/v1/admin/app-config/${id}`, patch);
    return unwrap(response.data);
};
